(function () {
  "use strict";

  // Global, shared cache across all component instances
  const RCC_CACHE = (window.RCC_COLLECTIONS_CACHE =
    window.RCC_COLLECTIONS_CACHE || {
      ttlMs: 30 * 60 * 1000, // 30 minutes
      productCollections: new Map(), // productGid -> { t, v }
      productCollectionsInflight: new Map(), // productGid -> Promise
      collectionProducts: new Map(), // collectionGid -> { t, v }
      collectionProductsInflight: new Map(), // collectionGid -> Promise
      productVariants: new Map(), // productGid -> { t, v } (for native variant mode)
      productVariantsInflight: new Map(), // productGid -> Promise
    });

  class ProductOptionsComponent extends HTMLElement {
    constructor() {
      super();
      this._lastProductIdKey = null;
      this._lastVariantKey = null;
      this._lastHtml = "";
      this._renderSeq = 0;
      this._inflight = null;
    }

    static get observedAttributes() {
      return ["product"];
    }

    get product() {
      return this._product;
    }

    set product(value) {
      console.log("RCC Product Options component initialized");
      console.log(this._product);
      const prevProductIdKey = this._lastProductIdKey;
      const prevVariantKey = this._lastVariantKey;
      this._product = value;
      if (!this._product) {
        this.innerHTML = "";
        return;
      }

      // Paint main image immediately for best perceived performance
      this.ensureContainer();
      // this.renderMainImage();

      const nextProductIdKey = this.productIdKey();
      const nextVariantKey = this.variantKey();
      const hasVariants =
        Array.isArray(this._product?.variants) ||
        Array.isArray(this._product?.variantsAll);

      if (!nextProductIdKey || !nextVariantKey || !hasVariants) {
        return;
      }

      this._lastProductIdKey = nextProductIdKey;
      this._lastVariantKey = nextVariantKey;
      const productChanged = prevProductIdKey !== nextProductIdKey;
      const variantChanged = prevVariantKey !== nextVariantKey;
      if (!productChanged && !variantChanged) return; // avoid redundant rerender
      if (!productChanged) {
        const list = this.querySelector(".rcc-color-thumbnails__list");
        if (list) {
          this._syncActiveThumb(list);
          return;
        }
      }
      this.render();
    }

    connectedCallback() {
      if (this._product) {
        // Ensure immediate main image paint on attach
        this.ensureContainer();
        this.renderMainImage();
        this.render();
      }
    }

    async render() {
      if (!this._product) {
        this.innerHTML = "";
        return;
      }

      // Ensure container exists; avoid full reflow on subsequent renders
      this.ensureContainer();

      try {
        const seq = ++this._renderSeq;
        // Update main image above the slider immediately before any async work
        this.renderMainImage();

        // Fetch collections for this product (id, handle, title) via shared cache
        const productCollections = await this.getProductCollectionsSimple();
        if (seq !== this._renderSeq) return; // stale
        const pdpCollections = this.filterPdpCollections(productCollections);

        if (!pdpCollections.length) {
          // Try native variant mode first
          const variantsHtml = await this.buildNativeVariantHtml();
          if (seq !== this._renderSeq) return; // stale
          if (variantsHtml) {
            this.renderHtmlIfChanged(variantsHtml);
            return;
          }
          // Ultimate fallback: still render a single thumbnail for this product itself
          const html = this.buildSelfThumbnailHtml();
          this.renderHtmlIfChanged(html);
          return;
        }
        // Fetch products/variants for each relevant collection via shared cache
        const collectionsProducts = await Promise.all(
          pdpCollections.map((c) => this.getCollectionProducts(c.id))
        );
        if (seq !== this._renderSeq) return; // stale

        const enriched = pdpCollections.map((c, i) => ({
          ...c,
          products: collectionsProducts[i] || [],
        }));
        const html = this.buildThumbnailsHtml(enriched);
        this.renderHtmlIfChanged(html);
      } catch (e) {
        console.error("Error rendering PDP color thumbnails:", e);
        const container = this.querySelector(".rcc-color-thumbnails");
        if (container) {
          container.innerHTML = "";
          container.setAttribute("data-state", "error");
        }
      }
    }

    // Ensure the base DOM exists (image container + thumbnails container)
    ensureContainer() {
      if (!this.querySelector(".product-custom-component")) {
        this.innerHTML = `
          <div class="product-custom-component">
            <div class="rcc-color-image"></div>
            <div class="rcc-color-thumbnails" data-state="loading"></div>
          </div>
        `;
      } else {
        const el = this.querySelector(".rcc-color-thumbnails");
        if (el && !el.hasChildNodes()) {
          el.setAttribute("data-state", "loading");
        }
      }
    }

    // Build thumbnails for native product variants (without PDP collections)
    async buildNativeVariantHtml() {
      const productIdCandidate =
        this._product?.productId ||
        this._product?.id ||
        this._product?.product?.id ||
        null;
      const productGid = this.toProductGid(productIdCandidate);
      if (!productGid) return "";

      const token = document.querySelector(".rcc-search")?.dataset.token;
      if (!token) return "";

      // Fetch product with variants
      const productData = await this.getProductWithVariants(productGid);
      if (!productData || !productData.variants || productData.variants.length <= 1) {
        return ""; // No variants or only one variant, don't show options
      }

      const shownImages = new Set();
      const items = [];
      const MAX_ITEMS = 10;

      const productHandle = productData.handle;

      for (const variant of productData.variants) {
        const variantIdNumeric = this.idFromGid(variant.id);

        const parentVariantIdNumeric = this._product?.variantId
        ? String(this._product.variantId)
        : this.idFromGid(this._product?.variant?.id);

        if (
          items.length >= MAX_ITEMS &&
          parentVariantIdNumeric != variantIdNumeric
        ) {
          continue;
        }

        if (!variant.availableForSale) continue;

        const imageId = variant.image?.id;
        if (!imageId || shownImages.has(imageId)) continue;
        shownImages.add(imageId);

        const href = productHandle
          ? `/products/${productHandle}${
              variantIdNumeric ? `?variant=${variantIdNumeric}` : ""
            }`
          : productData.onlineStoreUrl || "#";

        const rawUrl = variant.image?.url || "";
        const thumbUrl = this.imgURL(rawUrl, "66x66");
        const largeUrl = this.imgURL(rawUrl, "480x");
        const firstOptionValue =
          Array.isArray(variant.selectedOptions) && variant.selectedOptions.length
            ? variant.selectedOptions[0].value
            : "";
        const title = `${productData.title}${
          firstOptionValue
            ? ` - ${firstOptionValue}`
            : variant.title && variant.title !== "Default Title"
            ? ` - ${variant.title}`
            : ""
        }`.trim();
        const priceInfo = this._priceInfoForVariant(variant);
        const priceValue =
          priceInfo && priceInfo.price != null ? priceInfo.price : null;
        const originalValue =
          priceInfo && priceInfo.originalPrice != null
            ? priceInfo.originalPrice
            : priceValue;
        const currencyValue =
          priceInfo && priceInfo.currencyCode
            ? priceInfo.currencyCode
            : null;
        const priceAttr = priceValue
          ? ` data-price="${this.escapeHtml(priceValue)}"`
          : "";
        const originalAttr = originalValue
          ? ` data-original-price="${this.escapeHtml(originalValue)}"`
          : "";
        const currencyAttr = currencyValue
          ? ` data-currency-code="${this.escapeHtml(currencyValue)}"`
          : "";
        const productIdNumeric = this.idFromGid(productData.id);
        const productIdAttr = productIdNumeric
          ? ` data-product-id="${productIdNumeric}"`
          : "";
        const variantIdAttr = variantIdNumeric
          ? ` data-variant-id="${variantIdNumeric}"`
          : "";

        items.push(`
          <li class="rcc-color-thumbnails__item"${priceAttr}${originalAttr}${currencyAttr}${productIdAttr}${variantIdAttr}>
            <a href="${href}">
              <img
                src="${thumbUrl}"
                width="66"
                height="66"
                alt="${this.escapeHtml(title)}"
                data-src="${largeUrl}"
                data-title="${this.escapeHtml(productData.title)}${
          firstOptionValue
            ? ` - ${this.escapeHtml(firstOptionValue)}`
            : ""
        }"
                class="thumbnail-image"
              >
            </a>
          </li>
        `);

        // if (items.length >= MAX_ITEMS) {
        //   break;
        // }
      }

      if (!items.length) return "";
      return `
        <ul class="rcc-color-thumbnails__list">
          ${items.join("\n")}
        </ul>
      `;
    }

    // Build thumbnails markup similar to the Liquid snippet
    buildThumbnailsHtml(collections) {
      // Combine items across all matching collections into a single list
      const shownImages = new Set();
      const items = [];
      const MAX_ITEMS = 10;

      productLoop: for (const collection of collections || []) {
        const products = collection.products || [];

        for (const product of products) {
          const variants = product.variants || [];

          for (const variant of variants) {

            //here

            if (!variant.availableForSale) continue;

            const imageId = variant.image?.id;
            if (!imageId || shownImages.has(imageId)) continue;
            shownImages.add(imageId);

            const productHandle = product.handle;
            const variantIdNumeric = this.idFromGid(variant.id);
            const href = productHandle
              ? `/products/${productHandle}${
                  variantIdNumeric ? `?variant=${variantIdNumeric}` : ""
                }`
              : product.onlineStoreUrl || "#";

            const rawUrl = variant.image?.url || "";
            const thumbUrl = this.imgURL(rawUrl, "66x66");
            const largeUrl = this.imgURL(rawUrl, "480x");
            const title = `${product.title}${
              variant.firstOptionValue
                ? ` - ${variant.firstOptionValue}`
                : variant.title
                ? ` - ${variant.title}`
                : ""
            }`.trim();
            const priceInfo = this._priceInfoForVariant(variant);
            const priceValue =
              priceInfo && priceInfo.price != null ? priceInfo.price : null;
            const originalValue =
              priceInfo && priceInfo.originalPrice != null
                ? priceInfo.originalPrice
                : priceValue;
            const currencyValue =
              priceInfo && priceInfo.currencyCode
                ? priceInfo.currencyCode
                : null;
            const priceAttr = priceValue
              ? ` data-price="${this.escapeHtml(priceValue)}"`
              : "";
            const originalAttr = originalValue
              ? ` data-original-price="${this.escapeHtml(originalValue)}"`
              : "";
            const currencyAttr = currencyValue
              ? ` data-currency-code="${this.escapeHtml(currencyValue)}"`
              : "";
            const productIdNumeric = this.idFromGid(product.id);
            const productIdAttr = productIdNumeric
              ? ` data-product-id="${productIdNumeric}"`
              : "";
            const variantIdAttr = variantIdNumeric
              ? ` data-variant-id="${variantIdNumeric}"`
              : "";

            items.push(`
              <li class="rcc-color-thumbnails__item"${priceAttr}${originalAttr}${currencyAttr}${productIdAttr}${variantIdAttr}>
                <a href="${href}">
                  <img
                    src="${thumbUrl}"
                    width="66"
                    height="66"
                    alt="${this.escapeHtml(title)}"
                    data-src="${largeUrl}"
                    data-title="${this.escapeHtml(product.title)}${
              variant.firstOptionValue
                ? ` - ${this.escapeHtml(variant.firstOptionValue)}`
                : ""
            }"
                    class="thumbnail-image"
                  >
                </a>
              </li>
            `);
          }
        }
      }

      if (!items.length) return "";
      return `
        <ul class="rcc-color-thumbnails__list">
          ${items.join("\n")}
        </ul>
      `;
    }

    // Fallback: Build a single thumbnail for the product itself
    buildSelfThumbnailHtml() {
      const productTitle = this._product?.title || "";
      const productUrlRaw = this._product?.url || "#";
      const variantIdNumeric = this._product?.variantId
        ? String(this._product.variantId)
        : this.idFromGid(this._product?.variant?.id);

      let href = productUrlRaw || "#";
      try {
        if (variantIdNumeric) {
          const u = new URL(
            href,
            window.location?.origin || "https://dardanos.com"
          );
          if (!u.searchParams.get("variant")) {
            u.searchParams.set("variant", String(variantIdNumeric));
          }
          href = u.pathname + u.search + u.hash;
        }
      } catch (_) {
        // keep raw href on URL parsing failure
      }

      const rawUrl = this._product?.img?.uri || this._product?.img?.url || "";
      if (!rawUrl) return "";
      const thumbUrl = this.imgURL(rawUrl, "66x66");
      const largeUrl = this.imgURL(rawUrl, "480x");

      const variantLabel =
        this._product?.variant &&
        this._product.variant.title &&
        this._product.variant.title !== "Default Title"
          ? ` - ${this._product.variant.title}`
          : "";
      const title = `${productTitle}${variantLabel}`.trim();
      const primaryVariant =
        this._product?.variant?.node || this._product?.variant || null;
      const priceInfo =
        this._priceInfoForVariant(primaryVariant) ||
        this._priceInfoForVariant(this._product);
      const priceValue =
        priceInfo && priceInfo.price != null ? priceInfo.price : null;
      const originalValue =
        priceInfo && priceInfo.originalPrice != null
          ? priceInfo.originalPrice
          : priceValue;
      const currencyValue =
        priceInfo && priceInfo.currencyCode ? priceInfo.currencyCode : null;
      const priceAttr = priceValue
        ? ` data-price="${this.escapeHtml(priceValue)}"`
        : "";
      const originalAttr = originalValue
        ? ` data-original-price="${this.escapeHtml(originalValue)}"`
        : "";
      const currencyAttr = currencyValue
        ? ` data-currency-code="${this.escapeHtml(currencyValue)}"`
        : "";

      return `
        <ul class="rcc-color-thumbnails__list">
          <li class="rcc-color-thumbnails__item"${priceAttr}${originalAttr}${currencyAttr}>
            <a href="${this.escapeHtml(href)}">
              <img
                src="${this.escapeHtml(thumbUrl)}"
                width="66"
                height="66"
                alt="${this.escapeHtml(title || productTitle)}"
                data-src="${this.escapeHtml(largeUrl)}"
                data-title="${this.escapeHtml(title || productTitle)}"
                class="thumbnail-image"
              >
            </a>
          </li>
        </ul>
      `;
    }

    // Render the main/full-size image above the thumbnails
    renderMainImage() {
      const container = this.querySelector(".rcc-color-image");
      if (!container) {
        console.log(
          "[RCC] renderMainImage: No .rcc-color-image container found"
        );
        return;
      }
      const featuredImageUrl = this.getFeaturedImageUrl();
      const uri = featuredImageUrl
        ? this.imgURL(featuredImageUrl, "480x480")
        : this._product?.img?.uri || this._product?.img?.url || "";
      const title = this._product?.title || "";
      if (!uri) {
        container.innerHTML = "";
        return;
      }
      const alt = `${title ? this.escapeHtml(title) + " " : ""}Product Image`;
      const productIdNumeric =
        this._product?.productId || this.idFromGid(this._product?.id);
      const productUrl = this._product?.url || "";
      const variantIdNumeric =
        this._product?.variantId ||
        this.idFromGid(this._product?.variant?.id) ||
        "";

      // Check if product is on sale
      const primaryVariant =
        this._product?.variant?.node || this._product?.variant || null;
      const priceInfo =
        this._priceInfoForVariant(primaryVariant) ||
        this._priceInfoForVariant(this._product);
      const onSale =
        priceInfo &&
        priceInfo.price != null &&
        priceInfo.originalPrice != null &&
        parseFloat(priceInfo.originalPrice) > parseFloat(priceInfo.price);
      const saleBadge = onSale ? '<span class="rcc-search__product__tag rcc-search__product__tag--sale">SALE</span>' : '';

      // Use requested example markup and classes, wrapped in a link so URL updates
      container.innerHTML = `
        <div class="rcc-color-image__badges">
          <div class="klaviyo-favorites-plp-slot" data-product-id="${
            productIdNumeric || ""
          }" data-product-url="${this.escapeHtml(
            productUrl
          )}" data-variant-id="${variantIdNumeric}"></div>
          ${saleBadge}
        </div>
        <a class="rcc-color-image__link" href="${this.escapeHtml(
          productUrl
        )}" aria-label="${alt}">
          <img width="252.66" height="252.66" src="${uri}" alt="${alt}" fetchpriority="high" loading="eager" decoding="sync" class="rcc-search__image rcc-search__image--primary">
        </a>
      `;
    }

    // Setup interactivity: click a thumbnail to promote it to the main image and update link
    setupThumbInteractions() {
      const list = this.querySelector(".rcc-color-thumbnails__list");
      const { mainImg, mainLink } = this.getMainTargets();

      if (!list || !mainImg || !mainLink) return;

      if (list.dataset.bound === "1") {
        // Already bound; ensure active state matches current selection
        this._syncActiveThumb(list);
        return;
      }
      list.dataset.bound = "1";

      const items = Array.from(
        list.querySelectorAll(".rcc-color-thumbnails__item")
      );
      if (!items.length) return;

      const activate = (li) => {
        if (!li) return;
        this._applyThumbSelectionState(li);
      };

      items.forEach((li) => {
        const link = li.querySelector("a");
        const targetEl = link || li;

        const select = (ev) => {
          if (ev) {
            ev.preventDefault?.();
            ev.stopPropagation?.();
          }
          activate(li);
        };

        // Early intercept to avoid any theme-level navigation
        // targetEl.addEventListener("pointerdown", select, {
        //   passive: false,
        //   capture: true,
        // });
        // targetEl.addEventListener("touchstart", select, {
        //   passive: false,
        //   capture: true,
        // });
        targetEl.addEventListener("click", select, true);

        // Keyboard focus and Enter key support
        li.addEventListener("focusin", () => activate(li));
        targetEl.addEventListener("keydown", (ev) => {
          if (ev.key === "Enter" || ev.keyCode === 13 || ev.key === " ") {
            ev.preventDefault();
            activate(li);
          }
        });
      });

      // Choose the correct initial active based on variant/image match
      const initial = this._findInitialActiveThumb(items);
      if (initial) {
        this._applyThumbSelectionState(initial, {
          updateMainImage: !this.getFeaturedImageUrl(),
        });
      } else if (items[0]) {
        // No match found; just set the first item as active
        const first = items[0];
        items.forEach((it) => it.classList.toggle("is-active", it === first));

        // FIX: Force update the sale badge to match this first item
        // (This ensures the badge appears even if the initial render missed it)
        const price = first.getAttribute("data-price");
        const originalPrice = first.getAttribute("data-original-price");
        this._updateSaleBadge(price, originalPrice);
      }
    }

    // Re-resolve the main image and link inside this component or via parent product container
    getMainTargets() {
      let mainImg = this.querySelector(".rcc-color-image img");
      let mainLink = this.querySelector(".rcc-color-image a");
      const ok = (el) => el && document.contains(el);
      if (ok(mainImg) && ok(mainLink)) return { mainImg, mainLink };

      const pid = this._product?.productId
        ? String(this._product.productId)
        : null;
      const parent =
        (pid && document.getElementById(pid)) ||
        this.closest(".rcc-search__product");
      if (parent) {
        const scope = parent.querySelector("rcc-product-options") || parent;
        mainImg =
          scope.querySelector(".rcc-color-image img") ||
          parent.querySelector(".rcc-color-image img");
        mainLink =
          scope.querySelector(".rcc-color-image a") ||
          parent.querySelector(".rcc-color-image a");
      }
      return { mainImg, mainLink };
    }

    getFeaturedImageUrl() {
      const directValue = this._readMetafieldValue(
        this._product?.metafields,
        "custom",
        "featured_image_url"
      );
      if (directValue) return directValue;

      return this._readMetafieldValue(
        this._product?.product?.metafields,
        "custom",
        "featured_image_url"
      );
    }

    _readMetafieldValue(metafields, namespace, key) {
      if (!metafields) return "";

      if (Array.isArray(metafields)) {
        const match = metafields.find((item) => {
          return item && (item.value || item.reference);
        });
        return match?.value || match?.reference?.url || "";
      }

      if (typeof metafields === "object") {
        const namespaceEntry = metafields[namespace];
        if (
          namespaceEntry &&
          typeof namespaceEntry === "object" &&
          namespaceEntry[key] != null
        ) {
          const value = namespaceEntry[key];
          if (typeof value === "string") return value;
          if (value && typeof value === "object") {
            return value.value || value.url || value.src || "";
          }
        }

        const flatKey = `${namespace}.${key}`;
        const flatValue = metafields[flatKey];
        if (typeof flatValue === "string") return flatValue;
        if (flatValue && typeof flatValue === "object") {
          return flatValue.value || flatValue.url || flatValue.src || "";
        }
      }

      return "";
    }

    _applyThumbSelectionState(li, options = {}) {
      if (!li) return;
      const { updateMainImage = true } = options;
      const { mainImg: liveMainImg, mainLink: liveMainLink } =
        this.getMainTargets();
      const imgEl = li.querySelector("img.thumbnail-image");
      const aEl = li.querySelector("a");
      const list = li.parentElement;
      const items = list
        ? Array.from(list.querySelectorAll(".rcc-color-thumbnails__item"))
        : [];

      if (updateMainImage && liveMainImg && imgEl) {
        const largeSrc = imgEl.getAttribute("data-src") || imgEl.src;
        const title =
          imgEl.getAttribute("data-title") || imgEl.alt || liveMainImg.alt;
        if (largeSrc) liveMainImg.src = largeSrc;
        if (title) liveMainImg.alt = title;
      }

      if (liveMainLink && aEl) {
        const href =
          aEl.getAttribute("href") || liveMainLink.getAttribute("href");
        if (href) liveMainLink.href = href;
      }

      items.forEach((it) => it.classList.toggle("is-active", it === li));

      const price = li.getAttribute("data-price");
      const originalPrice = li.getAttribute("data-original-price");
      this._updateSaleBadge(price, originalPrice);

      const favoritesSlot = this.querySelector(".klaviyo-favorites-plp-slot");
      if (favoritesSlot) {
        const productId = li.getAttribute("data-product-id");
        const variantId = li.getAttribute("data-variant-id");
        const productUrl = aEl?.getAttribute("href");

        if (productId) {
          favoritesSlot.setAttribute("data-product-id", productId);
        }
        if (variantId) {
          favoritesSlot.setAttribute("data-variant-id", variantId);
        }
        if (productUrl) {
          favoritesSlot.setAttribute("data-product-url", productUrl);
        }
      }

      const href = aEl?.getAttribute("href");
      const productBlock = this.closest(".rcc-search__product");
      if (productBlock && href) {
        let productInfoLink = productBlock.querySelector(
          "a.rcc-search__product__block--vertical"
        );

        if (!productInfoLink) {
          productInfoLink = productBlock.querySelector(
            ".rcc-search__product__block--vertical a"
          );
        }

        if (!productInfoLink) {
          const priceEl = productBlock.querySelector(".rcc-search__product__price");
          if (priceEl) {
            productInfoLink = priceEl.closest("a");
          }
        }

        if (productInfoLink && productInfoLink.tagName === "A") {
          productInfoLink.href = href;
        }
      }

      this._applyPriceInfoFromThumb(li);

      if (productBlock) {
        productBlock.dispatchEvent(
          new CustomEvent("rcc:variant-change", {
            bubbles: true,
            detail: {
              variantId: li.getAttribute("data-variant-id") || "",
              productId: li.getAttribute("data-product-id") || "",
              href: href || "",
              price: price || "",
              originalPrice: originalPrice || ""
            }
          })
        );
      }
    }

    _normalizeImageUrl(u) {
      if (!u) return "";
      try {
        u = String(u).split("?")[0];
      } catch (_) {}
      return u.replace(
        /_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master|\d+x\d*|\d*x\d+)\.(jpg|jpeg|png|webp|gif)$/i,
        ".$2"
      );
    }

    _findInitialActiveThumb(items) {
      const { mainImg } = this.getMainTargets();
      // 1) Match by current product variant id
      const variantIdNumeric = this._product?.variantId
        ? String(this._product.variantId)
        : this.idFromGid(this._product?.variant?.id);
      if (variantIdNumeric) {
        for (const li of items) {
          const href = li.querySelector("a")?.getAttribute("href") || "";
          try {
            const url = new URL(
              href,
              window.location?.origin || "https://dardanos.com"
            );
            const v = url.searchParams.get("variant");
            if (v && String(v) === String(variantIdNumeric)) return li;
          } catch (_) {}
        }
      }

      // 2) Fallback: match by normalized image URL
      const currentSrc = this._normalizeImageUrl(mainImg?.getAttribute("src"));
      if (currentSrc) {
        for (const li of items) {
          const imgEl = li.querySelector("img.thumbnail-image");
          const cand = this._normalizeImageUrl(
            imgEl?.getAttribute("data-src") || imgEl?.getAttribute("src")
          );
          if (cand && cand === currentSrc) return li;
        }
      }
      return null;
    }

    _syncActiveThumb(list) {
      const { mainImg, mainLink } = this.getMainTargets();
      const items = Array.from(
        list.querySelectorAll(".rcc-color-thumbnails__item")
      );
      if (!items.length || !mainImg || !mainLink) return;
      const match = this._findInitialActiveThumb(items);
      if (!match) return;
      this._applyThumbSelectionState(match, {
        updateMainImage: !this.getFeaturedImageUrl(),
      });
    }

    _applyPriceInfoFromThumb(li) {
      if (
        !li ||
        !this._product ||
        typeof this._product.setPriceInfo !== "function"
      ) {
        return;
      }
      const price = li.getAttribute("data-price");
      const original = li.getAttribute("data-original-price");
      const currencyAttr = li.getAttribute("data-currency-code");
      const fallbackCurrency =
        this._product?.variant?.node?.price?.currencyCode ||
        this._product?.variant?.price?.currencyCode ||
        this._product?.variant?.node?.priceV2?.currencyCode ||
        this._product?.variant?.priceV2?.currencyCode ||
        this._product?.variant?.currencyCode ||
        this._product?.currencyCode ||
        null;
      const currencyCode = currencyAttr || fallbackCurrency;
      if (!price || !currencyCode) return;

      const current = this._product.priceInfo || {};
      const normalizedPrice = String(price);
      const normalizedOriginal = String(original || price);
      const normalizedCurrency = currencyCode || null;
      const currentPrice =
        current.price != null ? String(current.price) : null;
      const currentOriginal =
        current.originalPrice != null
          ? String(current.originalPrice)
          : currentPrice;
      const currentCurrency = current.currencyCode || null;

      if (
        currentPrice === normalizedPrice &&
        currentOriginal === normalizedOriginal &&
        currentCurrency === normalizedCurrency
      ) {
        this._updateSaleBadge(normalizedPrice, normalizedOriginal);
        return;
      }

      this._product.setPriceInfo({
        price,
        originalPrice: original || price,
        currencyCode,
      });
      this._updateSaleBadge(price, original || price);
    }

    _updateSaleBadge(price, originalPrice) {
      const badgesContainer = this.querySelector(".rcc-color-image__badges");
      if (!badgesContainer) return;
      const normalizedPrice = parseFloat(price);
      const normalizedOriginal = parseFloat(originalPrice);
      const onSale =
        !Number.isNaN(normalizedPrice) &&
        !Number.isNaN(normalizedOriginal) &&
        normalizedOriginal > normalizedPrice;
      let saleBadge = badgesContainer.querySelector(
        ".rcc-search__product__tag--sale"
      );
      if (onSale) {
        if (!saleBadge) {
          saleBadge = document.createElement("span");
          saleBadge.className =
            "rcc-search__product__tag rcc-search__product__tag--sale";
          saleBadge.textContent = "SALE";
          badgesContainer.appendChild(saleBadge);
        }
      } else if (saleBadge) {
        saleBadge.remove();
      }
    }

    _priceInfoForVariant(variant) {
      if (!variant || typeof variant !== "object") return null;

      const normalizeMoney = (value) => {
        if (!value) return { amount: null, currencyCode: null };
        if (typeof value === "object") {
          const amount =
            value.amount != null ? String(value.amount) : value.amount || null;
          const currencyCode = value.currencyCode || null;
          return { amount, currencyCode };
        }
        return { amount: String(value), currencyCode: null };
      };

      const pickMoney = (...candidates) => {
        let fallback = null;
        for (const candidate of candidates) {
          if (candidate == null) continue;
          const normalized = normalizeMoney(candidate);
          if (!normalized) continue;
          if (normalized.amount != null) {
            return normalized;
          }
          if (!fallback) {
            fallback = normalized;
          }
        }
        return fallback;
      };

      const price = pickMoney(
        variant.price,
        variant.priceV2,
        variant.price_amount,
        variant.priceAmount,
        variant.price_min,
        variant.priceMin,
        variant.min_price,
        variant.minPrice,
        variant.price_max,
        variant.priceMax,
        variant.max_price,
        variant.maxPrice,
        variant.priceRange?.minVariantPrice,
        variant.priceRange?.maxVariantPrice,
        variant.price_range?.min_price,
        variant.price_range?.max_price
      );
      const compare = pickMoney(
        variant.compareAtPrice,
        variant.compareAtPriceV2,
        variant.compare_at_price,
        variant.compare_at_price_min,
        variant.compare_at_price_max,
        variant.compareAtPriceMin,
        variant.compareAtPriceMax,
        variant.compareAtPriceRange?.minVariantPrice,
        variant.compareAtPriceRange?.maxVariantPrice,
        variant.compare_at_price_range?.min_price,
        variant.compare_at_price_range?.max_price
      );

      const amount = price?.amount ?? null;
      const compareAmount = compare?.amount ?? null;
      const currencyCode =
        price?.currencyCode ||
        compare?.currencyCode ||
        variant.currencyCode ||
        null;

      if (!amount && !compareAmount && !currencyCode) return null;

      return {
        price: amount,
        originalPrice: compareAmount || amount,
        currencyCode,
      };
    }

    // Shopify image URL size helper based on provided implementation
    imgURL(src, size) {
      if (!src) return "";

      let url = src;

      // For URLs with .progressive in them, we need to keep .progressive but clean up the _N suffix
      // Example: .progressive_1_-Photoroom.png becomes .progressive-Photoroom.png
      url = url.replace(/\.progressive_\d+_/g, ".progressive");
      url = url.replace(/\.progressive_\d+-/g, ".progressive-");

      // Strip any trailing Shopify size parameter right before the extension
      // This handles cases where the API already added a size like _480x.png
      url = url.replace(
        /_(\d+x\d*|\d*x\d+)(\.[^.]+)(\?|$)/g,
        "$2$3"
      );

      // Add the requested size parameter before the extension
      return url.replace(
        /(\.(jpg|png|webp|gif|jpeg))(\?|$)/g,
        function (_match, ext, _extName, queryOrEnd) {
          return "_" + size + ext + queryOrEnd;
        }
      );
    }

    filterPdpCollections(collections) {
      return (collections || []).filter((c) => (c.title || "").includes("PDP"));
    }

    renderHtmlIfChanged(html) {
      const container = this.querySelector(".rcc-color-thumbnails");
      if (!container) return;
      const changed = html !== this._lastHtml;
      if (changed) {
        container.innerHTML = html;
        container.removeAttribute("data-state");
        this._lastHtml = html;
        // Set up scroll arrows if content overflows
        this.setupScrollArrows();
      }
      // Ensure interactions are (idempotently) bound even if HTML didn't change (e.g. from cache)
      this.setupThumbInteractions();
    }

    escapeHtml(str) {
      if (!str) return "";
      return String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    }

    idFromGid(gid) {
      if (!gid || typeof gid !== "string") return null;
      const parts = gid.split("/");
      const last = parts[parts.length - 1];
      const num = parseInt(last, 10);
      return Number.isFinite(num) ? num : null;
    }

    toProductGid(idOrGid) {
      if (!idOrGid) return null;
      const s = String(idOrGid);
      if (s.startsWith("gid://")) return s;
      return `gid://shopify/Product/${s}`;
    }

    productKey() {
      const gid = this.productIdKey();
      const variantId = this.variantKey();
      return `${gid}|${variantId}`;
    }

    productIdKey() {
      return (
        this.toProductGid(
          this._product?.productId ||
            this._product?.id ||
            this._product?.product?.id
        ) || ""
      );
    }

    variantKey() {
      const variantId =
        this._product?.variantId || this._product?.variant?.id || null;
      return variantId == null ? "" : String(variantId);
    }

    // Shared-cache backed: fetch product with variants for native variant display
    async getProductWithVariants(productGid, variantsFirst = 50) {
      if (!productGid) return null;
      const token = document.querySelector(".rcc-search")?.dataset.token;
      if (!token) return null;

      // Check in-memory cache
      if (!RCC_CACHE.productVariants) {
        RCC_CACHE.productVariants = new Map();
        RCC_CACHE.productVariantsInflight = new Map();
      }

      const mem = RCC_CACHE.productVariants.get(productGid);
      if (mem && Date.now() - mem.t < RCC_CACHE.ttlMs) return mem.v;

      // Check localStorage cache
      const ls = this.localGet(
        `rcc:pdp:prodVariants:${productGid}`,
        RCC_CACHE.ttlMs
      );
      if (ls) {
        RCC_CACHE.productVariants.set(productGid, { t: Date.now(), v: ls });
        return ls;
      }

      // De-dupe concurrent calls
      if (RCC_CACHE.productVariantsInflight.has(productGid)) {
        return RCC_CACHE.productVariantsInflight.get(productGid);
      }

      const query = `
        query ProductVariants($productId: ID!, $variantsFirst: Int = 50) {
          product(id: $productId) {
            id
            title
            handle
            onlineStoreUrl
            variants(first: $variantsFirst) {
              edges {
                node {
                  id
                  title
                  availableForSale
                  price {
                    amount
                    currencyCode
                  }
                  compareAtPrice {
                    amount
                    currencyCode
                  }
                  selectedOptions {
                    name
                    value
                  }
                  image {
                    id
                    url
                  }
                }
              }
            }
          }
        }
      `;

      const p = fetch("/api/2025-07/graphql.json", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": token,
        },
        body: JSON.stringify({
          query,
          variables: { productId: productGid, variantsFirst },
        }),
      })
        .then((r) => r.json())
        .then((data) => {
          const product = data?.data?.product;
          if (!product) return null;

          const variantEdges = product?.variants?.edges || [];
          const variants = variantEdges.map((ve) => ve?.node || {});

          const v = {
            id: product.id,
            title: product.title,
            handle: product.handle,
            onlineStoreUrl: product.onlineStoreUrl,
            variants,
          };

          RCC_CACHE.productVariants.set(productGid, { t: Date.now(), v });
          this.localSet(`rcc:pdp:prodVariants:${productGid}`, v);
          return v;
        })
        .catch((err) => {
          console.error("Error fetching product variants:", err);
          return null;
        })
        .finally(() => {
          RCC_CACHE.productVariantsInflight.delete(productGid);
        });

      RCC_CACHE.productVariantsInflight.set(productGid, p);
      return p;
    }

    // Shared-cache backed: fetch only product's collections (ids, handles, titles)
    async getProductCollectionsSimple() {
      const productIdCandidate =
        this._product?.productId ||
        this._product?.id ||
        this._product?.product?.id ||
        null;
      const productGid = this.toProductGid(productIdCandidate);
      if (!productGid) return [];

      const token = document.querySelector(".rcc-search")?.dataset.token;
      if (!token) return [];

      // In-memory fresh cache
      const mem = RCC_CACHE.productCollections.get(productGid);
      if (mem && Date.now() - mem.t < RCC_CACHE.ttlMs) return mem.v;

      // localStorage cache
      const ls = this.localGet(
        `rcc:pdp:prodCols:${productGid}`,
        RCC_CACHE.ttlMs
      );
      if (ls) {
        RCC_CACHE.productCollections.set(productGid, { t: Date.now(), v: ls });
        return ls;
      }

      // de-dupe concurrent calls
      if (RCC_CACHE.productCollectionsInflight.has(productGid)) {
        return RCC_CACHE.productCollectionsInflight.get(productGid);
      }

      const query = `
        query ProductCollections($productId: ID!, $first: Int = 50) {
          product(id: $productId) {
            id
            collections(first: $first) { edges { node { id handle title } } }
          }
        }
      `;

      const p = fetch("/api/2025-07/graphql.json", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": token,
        },
        body: JSON.stringify({
          query,
          variables: { productId: productGid, first: 50 },
        }),
      })
        .then((r) => r.json())
        .then((data) => {
          const edges = data?.data?.product?.collections?.edges || [];
          const v = edges.map((e) => e?.node || {}).filter(Boolean);
          RCC_CACHE.productCollections.set(productGid, { t: Date.now(), v });
          this.localSet(`rcc:pdp:prodCols:${productGid}`, v);
          return v;
        })
        .catch((err) => {
          console.error("Error fetching product collections:", err);
          return [];
        })
        .finally(() => {
          RCC_CACHE.productCollectionsInflight.delete(productGid);
        });

      RCC_CACHE.productCollectionsInflight.set(productGid, p);
      return p;
    }

    // Shared-cache backed: fetch products/variants for a collection id
    async getCollectionProducts(
      collectionGid,
      productsFirst = 50,
      variantsFirst = 20
    ) {
      if (!collectionGid) return [];
      const token = document.querySelector(".rcc-search")?.dataset.token;
      if (!token) return [];

      const mem = RCC_CACHE.collectionProducts.get(collectionGid);
      if (mem && Date.now() - mem.t < RCC_CACHE.ttlMs) return mem.v;

      const ls = this.localGet(
        `rcc:pdp:colProds:${collectionGid}`,
        RCC_CACHE.ttlMs
      );
      if (ls) {
        RCC_CACHE.collectionProducts.set(collectionGid, {
          t: Date.now(),
          v: ls,
        });
        return ls;
      }

      if (RCC_CACHE.collectionProductsInflight.has(collectionGid)) {
        return RCC_CACHE.collectionProductsInflight.get(collectionGid);
      }

      const query = `
        query CollectionProducts($collectionId: ID!, $productsFirst: Int = 50, $variantsFirst: Int = 20) {
          collection(id: $collectionId) {
            id
            handle
            title
            products(first: $productsFirst) {
              edges {
                node {
                  id
                  title
                  handle
                  onlineStoreUrl
                  variants(first: $variantsFirst) {
                    edges { node { id title availableForSale price {
                amount
                currencyCode
              }
              compareAtPrice {
                amount
                currencyCode
              } selectedOptions { name value } image { id url } } }
                  }
                }
              }
            }
          }
        }
      `;

      const p = fetch("/api/2025-07/graphql.json", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": token,
        },
        body: JSON.stringify({
          query,
          variables: {
            collectionId: collectionGid,
            productsFirst,
            variantsFirst,
          },
        }),
      })
        .then((r) => r.json())
        .then((data) => {
          const productEdges = data?.data?.collection?.products?.edges || [];
          const products = productEdges.map((pe) => {
            const p = pe?.node || {};
            const variantEdges = p?.variants?.edges || [];
            const variants = variantEdges.map((ve) => {
              const v = ve?.node || {};
              const firstOptionValue =
                Array.isArray(v.selectedOptions) && v.selectedOptions.length
                  ? v.selectedOptions[0].value
                  : "";
              return { ...v, firstOptionValue };
            });
            return { ...p, variants };
          });
          RCC_CACHE.collectionProducts.set(collectionGid, {
            t: Date.now(),
            v: products,
          });
          this.localSet(`rcc:pdp:colProds:${collectionGid}`, products);
          return products;
        })
        .catch((err) => {
          console.error("Error fetching collection products:", err);
          return [];
        })
        .finally(() => {
          RCC_CACHE.collectionProductsInflight.delete(collectionGid);
        });

      RCC_CACHE.collectionProductsInflight.set(collectionGid, p);
      return p;
    }

    // localStorage helpers with TTL
    localGet(key, ttlMs) {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const obj = JSON.parse(raw);
        if (!obj || !obj.t) return null;
        if (Date.now() - obj.t > (ttlMs || RCC_CACHE.ttlMs)) return null;
        return obj.v;
      } catch (_) {
        return null;
      }
    }

    localSet(key, value) {
      try {
        localStorage.setItem(key, JSON.stringify({ t: Date.now(), v: value }));
      } catch (_) {
        // ignore storage errors
      }
    }

    async getVariantRRP() {
      if (!this._product?.variantId) {
        return null;
      }

      const token = document.querySelector(".rcc-search")?.dataset.token;
      if (!token) {
        console.warn("Storefront API token not found");
        return null;
      }

      const variantGid = `gid://shopify/ProductVariant/${this._product.variantId}`;

      const query = `
      query getVariantMetafield($id: ID!) {
        node(id: $id) {
          ... on ProductVariant {
            metafield(namespace: "custom", key: "variant_rrp") {
              value
            }
          }
        }
      }
    `;

      try {
        const response = await fetch("/api/2025-07/graphql.json", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": token,
          },
          body: JSON.stringify({
            query,
            variables: { id: variantGid },
          }),
        });

        const data = await response.json();
        return data?.data?.node?.metafield?.value || null;
      } catch (error) {
        console.error("Error fetching RRP value:", error);
        return null;
      }
    }
  }

  customElements.define("rcc-product-options", ProductOptionsComponent);

  // --- Enhancements: Horizontal scroll arrows for overflowing thumbnails ---
  ProductOptionsComponent.prototype.setupScrollArrows = function () {
    const wrap = this.querySelector(".rcc-color-thumbnails");
    const list = this.querySelector(".rcc-color-thumbnails__list");
    if (!wrap || !list) return;

    // avoid duplicate controls on rerender
    wrap.querySelectorAll(".rcc-thumbs-arrow").forEach((el) => el.remove());
    wrap.classList.add("rcc-color-thumbnails--with-arrows");

    const makeBtn = (dir) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = `rcc-thumbs-arrow rcc-thumbs-arrow--${dir}`;
      btn.setAttribute(
        "aria-label",
        dir === "left" ? "Scroll left" : "Scroll right"
      );
      btn.innerHTML = `
        <svg class="rcc-thumbs-arrow__svg" viewBox="0 0 120 120" width="22" height="22" aria-hidden="true" focusable="false">
          <path d="M 10,50 L 60,100 L 70,90 L 30,50  L 70,10 L 60,0 Z" class="arrow" ${
            dir === "right" ? 'transform="translate(100, 100) rotate(180)"' : ""
          }></path>
        </svg>
      `;

      btn.addEventListener("click", () => {
        const item = list.querySelector(".rcc-color-thumbnails__item");
        const step = item ? item.getBoundingClientRect().width + 10 : 80; // 10 = gap fallback
        const delta = dir === "left" ? -step : step;
        list.scrollBy({ left: delta, behavior: "smooth" });
      });
      return btn;
    };

    const leftBtn = makeBtn("left");
    const rightBtn = makeBtn("right");
    wrap.appendChild(leftBtn);
    wrap.appendChild(rightBtn);

    const update = () => {
      // Only show arrows when overflow exists
      const hasOverflow = list.scrollWidth > list.clientWidth + 2; // tolerance
      leftBtn.style.display = hasOverflow ? "flex" : "none";
      rightBtn.style.display = hasOverflow ? "flex" : "none";
      if (!hasOverflow) return;

      const atStart = list.scrollLeft <= 1;
      const atEnd = list.scrollLeft + list.clientWidth >= list.scrollWidth - 1;
      leftBtn.classList.toggle("is-disabled", atStart);
      rightBtn.classList.toggle("is-disabled", atEnd);
    };

    // Observe scroll and resize
    list.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(list);

    // Keep reference to clean up on future re-renders
    if (this._arrowsCleanup) this._arrowsCleanup();
    this._arrowsCleanup = () => {
      try {
        ro.disconnect();
      } catch (_) {}
      list.removeEventListener("scroll", update);
    };

    // Initial state
    // Defer to next frame to ensure layout is settled
    requestAnimationFrame(update);
  };
    // 
  // --- Evan's Enhancement: Replace width abbreviations with full names ---
  (function replaceWidthLabels() {
    const widthMap = {
      'N': 'Narrow',
      'M': 'Medium',
      'W': 'Wide',
      'XW': 'Extra Wide',
      'XXW': 'Extra Extra Wide'
    };

    function updateWidthLabels() {
      const container = document.querySelector('#attributesoptionWidth-content');
      if (!container) return;

      const labels = container.querySelectorAll('.rcc-search__filter__title');
      labels.forEach(label => {
        const countSpan = label.querySelector('.rcc-search__filter__count');
        if (!countSpan) return;

        const code = label.textContent.replace(countSpan.textContent, '').trim();
        if (widthMap[code]) {
          label.firstChild.textContent = widthMap[code] + ' ';
        }
      });
    }

    function observeNimstrata() {
      const target = document.querySelector('#rcc-search') || document.body;
      if (!target) {
        // Retry until Nimstrata has mounted
        return setTimeout(observeNimstrata, 100);
      }

      // Run once immediately
      updateWidthLabels();

      // Observe for Nimstrata re-render events
      const observer = new MutationObserver(mutations => {
        for (const m of mutations) {
          if (m.addedNodes.length || m.removedNodes.length) {
            updateWidthLabels();
          }
        }
      });

      observer.observe(target, {
        childList: true,
        subtree: true
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', observeNimstrata);
    } else {
      observeNimstrata();
    }
  })();

})();


(function () {
  const RCC_RECENT_WIDGET_SELECTOR = '.rcc-recommendations[data-template="product"]';
  const RCC_RECENT_PRODUCT_SELECTOR = '.rcc-search__product--recs';
  const RCC_RECENT_CACHE = new Map();
  let scheduledRefresh = false;

  function escapeHtml(value) {
    if (value == null) return "";
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function parseVariantIdFromHref(href) {
    if (!href) return "";
    try {
      const url = new URL(href, window.location.origin);
      return url.searchParams.get("variant") || "";
    } catch (_) {
      return "";
    }
  }

  function getProductHandle(article) {
    if (!article) return "";
    const link =
      article.querySelector(".rcc-search__product__info") ||
      article.querySelector(".rcc-color-image__link") ||
      article.querySelector("a[href*='/products/']");
    const href = link?.getAttribute("href") || "";
    const match = href.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : "";
  }

  function getCurrentVariantId(article) {
    if (!article) return "";
    const activeThumb = article.querySelector(".rcc-color-thumbnails__item.is-active");
    if (activeThumb?.getAttribute("data-variant-id")) {
      return activeThumb.getAttribute("data-variant-id");
    }

    const favoritesSlot = article.querySelector(".klaviyo-favorites-plp-slot");
    if (favoritesSlot?.getAttribute("data-variant-id")) {
      return favoritesSlot.getAttribute("data-variant-id");
    }

    const mainLink = article.querySelector(".rcc-color-image__link");
    const infoLink = article.querySelector(".rcc-search__product__info");
    return (
      parseVariantIdFromHref(mainLink?.getAttribute("href")) ||
      parseVariantIdFromHref(infoLink?.getAttribute("href")) ||
      ""
    );
  }

  function normalizeOptionName(optionName) {
    return String(optionName || "").trim().toLowerCase();
  }

  function isColorOption(optionName) {
    const normalized = normalizeOptionName(optionName);
    return normalized.includes("color") || normalized.includes("colour");
  }

  function getOptionDefinitions(productData) {
    if (!productData || !Array.isArray(productData.options)) {
      return [];
    }

    return productData.options
      .map((option, index) => {
        if (typeof option === "string") {
          return {
            index: index + 1,
            name: option
          };
        }

        if (option && typeof option === "object") {
          return {
            index: Number(option.position) || index + 1,
            name:
              (typeof option.name === "string" && option.name) ||
              (typeof option.label === "string" && option.label) ||
              (typeof option.title === "string" && option.title) ||
              `Option ${index + 1}`
          };
        }

        return {
          index: index + 1,
          name: `Option ${index + 1}`
        };
      })
      .sort((firstOption, secondOption) => firstOption.index - secondOption.index);
  }

  async function fetchProductJson(handle) {
    if (!handle) return null;

    const existing = RCC_RECENT_CACHE.get(handle);
    if (existing) {
      return existing instanceof Promise ? existing : Promise.resolve(existing);
    }

    const request = fetch(`/products/${handle}.js`, { credentials: "same-origin" })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load product JSON for ${handle}`);
        }
        return response.json();
      })
      .then((product) => {
        RCC_RECENT_CACHE.set(handle, product);
        return product;
      })
      .catch((error) => {
        RCC_RECENT_CACHE.delete(handle);
        console.error("[RCC recent] product fetch failed", error);
        return null;
      });

    RCC_RECENT_CACHE.set(handle, request);
    return request;
  }

  function getCurrentVariant(productData, article) {
    if (!productData || !Array.isArray(productData.variants)) return null;

    const currentVariantId = getCurrentVariantId(article);
    if (currentVariantId) {
      const matchedVariant = productData.variants.find(
        (variant) => String(variant.id) === String(currentVariantId)
      );
      if (matchedVariant) return matchedVariant;
    }

    return (
      productData.variants.find((variant) => variant.available) ||
      productData.variants[0] ||
      null
    );
  }

  function getRenderableOptionGroups(productData, currentVariant) {
    if (!productData || !Array.isArray(productData.options) || !Array.isArray(productData.variants)) {
      return [];
    }

    const optionDefinitions = getOptionDefinitions(productData);
    const colorOption = optionDefinitions.find((option) => isColorOption(option.name)) || null;
    const availableVariants = productData.variants.filter((variant) => variant.available);
    const currentColorValue =
      colorOption && currentVariant ? currentVariant[`option${colorOption.index}`] : "";

    const scopedVariants =
      colorOption && currentColorValue
        ? availableVariants.filter(
            (variant) => variant[`option${colorOption.index}`] === currentColorValue
          )
        : availableVariants;

    return optionDefinitions
      .filter((option) => !colorOption || option.index !== colorOption.index)
      .map((option) => {
        const values = Array.from(
          new Set(
            scopedVariants
              .map((variant) => variant[`option${option.index}`])
              .filter(Boolean)
          )
        );
        return { ...option, values };
      })
      .filter((option) => option.values.length > 0);
  }

  function getInitialSelections(optionGroups) {
    return optionGroups.reduce((selections, optionGroup) => {
      selections[optionGroup.name] = optionGroup.values.length === 1 ? optionGroup.values[0] : "";
      return selections;
    }, {});
  }

  function findMatchingVariant(productData, currentVariant, selections) {
    if (!productData || !Array.isArray(productData.options) || !Array.isArray(productData.variants)) {
      return null;
    }

    const optionDefinitions = getOptionDefinitions(productData);
    const colorOption = optionDefinitions.find((option) => isColorOption(option.name)) || null;
    const currentColorValue =
      colorOption && currentVariant ? currentVariant[`option${colorOption.index}`] : "";

    return (
      productData.variants.find((variant) => {
        if (!variant.available) return false;

        if (
          colorOption &&
          currentColorValue &&
          variant[`option${colorOption.index}`] !== currentColorValue
        ) {
          return false;
        }

        return optionDefinitions.every((option) => {
          const optionValue = variant[`option${option.index}`];
          if (colorOption && option.index === colorOption.index) {
            return true;
          }

          const desiredValue = selections[option.name];
          if (desiredValue == null || desiredValue === "") {
            return false;
          }

          return desiredValue === optionValue;
        });
      }) || null
    );
  }

  function getAvailableValuesForOption(productData, currentVariant, selections, targetOptionName) {
    if (!productData || !Array.isArray(productData.variants)) {
      return new Set();
    }

    const optionDefinitions = getOptionDefinitions(productData);
    const targetOption = optionDefinitions.find((option) => option.name === targetOptionName);
    if (!targetOption) {
      return new Set();
    }

    const colorOption = optionDefinitions.find((option) => isColorOption(option.name)) || null;
    const currentColorValue =
      colorOption && currentVariant ? currentVariant[`option${colorOption.index}`] : "";

    return new Set(
      productData.variants
        .filter((variant) => {
          if (!variant.available) return false;

          if (
            colorOption &&
            currentColorValue &&
            variant[`option${colorOption.index}`] !== currentColorValue
          ) {
            return false;
          }

          return optionDefinitions.every((option) => {
            if (colorOption && option.index === colorOption.index) {
              return true;
            }

            if (option.name === targetOptionName) {
              return true;
            }

            const desiredValue = selections[option.name];
            if (desiredValue == null || desiredValue === "") {
              return true;
            }

            return desiredValue === variant[`option${option.index}`];
          });
        })
        .map((variant) => variant[`option${targetOption.index}`])
        .filter(Boolean)
    );
  }

  function collectFormSelections(form, optionGroups) {
    const selections = getInitialSelections(optionGroups);
    form.querySelectorAll("[data-rcc-option-name]").forEach((select) => {
      const optionName = select.getAttribute("data-rcc-option-name");
      if (!optionName) return;
      selections[optionName] = select.value;
    });
    return selections;
  }

  function syncOptionAvailability(form, productData, currentVariant, optionGroups) {
    if (!form) {
      return getInitialSelections(optionGroups);
    }

    let selections = collectFormSelections(form, optionGroups);

    form.querySelectorAll("[data-rcc-option-name]").forEach((select) => {
      const optionName = select.getAttribute("data-rcc-option-name");
      if (!optionName) return;

      const availableValues = getAvailableValuesForOption(
        productData,
        currentVariant,
        selections,
        optionName
      );

      Array.from(select.options).forEach((option) => {
        if (!option.value) {
          option.disabled = false;
          return;
        }

        option.disabled = !availableValues.has(option.value);
      });

      if (select.value) {
        const selectedOption = select.options[select.selectedIndex];
        if (selectedOption && selectedOption.disabled) {
          select.value = "";
        }
      }
    });

    selections = collectFormSelections(form, optionGroups);

    form.querySelectorAll("[data-rcc-option-name]").forEach((select) => {
      const optionName = select.getAttribute("data-rcc-option-name");
      if (!optionName) return;

      const availableValues = getAvailableValuesForOption(
        productData,
        currentVariant,
        selections,
        optionName
      );

      Array.from(select.options).forEach((option) => {
        if (!option.value) {
          option.disabled = false;
          return;
        }

        option.disabled = !availableValues.has(option.value);
      });
    });

    return selections;
  }

  function buildSelectionPrompt(missingOptions) {
    if (!missingOptions.length) return "Choose an available option combination.";
    if (missingOptions.length === 1) return `Select ${missingOptions[0]}.`;
    if (missingOptions.length === 2) return `Select ${missingOptions[0]} and ${missingOptions[1]}.`;
    return `Select ${missingOptions.slice(0, -1).join(", ")}, and ${missingOptions[missingOptions.length - 1]}.`;
  }

  function renderOptionSelectors(optionGroups, initialSelections) {
    if (!optionGroups.length) return "";

    return `
      <div class="rcc-recsai__options">
        ${optionGroups
          .map(
            (optionGroup) => `
              <label class="rcc-recsai__option-group">
                <span class="rcc-recsai__option-label">${escapeHtml(optionGroup.name)}</span>
                <select class="rcc-recsai__option-select" data-rcc-option-name="${escapeHtml(optionGroup.name)}">
                  <option value="">Select ${escapeHtml(optionGroup.name)}</option>
                  ${optionGroup.values
                    .map(
                      (optionValue) => {
                        const isSelected = initialSelections?.[optionGroup.name] === optionValue;
                        return `<option value="${escapeHtml(optionValue)}"${isSelected ? " selected" : ""}>${escapeHtml(optionValue)}</option>`;
                      }
                    )
                    .join("")}
                </select>
              </label>
            `
          )
          .join("")}
      </div>
    `;
  }

  function getAddToCartLabel() {
    return window.PXUTheme?.translation?.add_to_cart || "Add To Cart";
  }

  function buildPurchaseSignature(handle, currentVariant, optionGroups) {
    return [
      handle || "",
      currentVariant ? String(currentVariant.id) : "",
      optionGroups
        .map((optionGroup) => `${optionGroup.name}:${optionGroup.values.join("|")}`)
        .join("||")
    ].join("::");
  }

  function getOrCreatePurchaseContainer(article) {
    let purchaseContainer = article.querySelector(".rcc-recsai__purchase");
    if (purchaseContainer) return purchaseContainer;

    purchaseContainer = document.createElement("div");
    purchaseContainer.className = "rcc-recsai__purchase";

    const infoLink = article.querySelector(".rcc-search__product__info");
    if (infoLink && infoLink.parentNode === article) {
      infoLink.insertAdjacentElement("afterend", purchaseContainer);
    } else {
      article.appendChild(purchaseContainer);
    }

    return purchaseContainer;
  }

  async function renderPurchaseForm(article) {
    const handle = getProductHandle(article);
    if (!handle) return;

    const productData = await fetchProductJson(handle);
    if (!productData) return;

    const currentVariant = getCurrentVariant(productData, article);
    const optionGroups = getRenderableOptionGroups(productData, currentVariant);
    const initialSelections = getInitialSelections(optionGroups);
    const selectableOptions = optionGroups.filter((optionGroup) => optionGroup.values.length > 1);
    const requiresSelection = selectableOptions.length > 0;
    const selectedVariant = findMatchingVariant(productData, currentVariant, initialSelections);
    const purchaseContainer = getOrCreatePurchaseContainer(article);
    const purchaseSignature = buildPurchaseSignature(handle, currentVariant, optionGroups);
    const selectionMessage = requiresSelection
      ? buildSelectionPrompt(selectableOptions.map((optionGroup) => optionGroup.name))
      : "";
    const addToCartLabel = getAddToCartLabel();

    if (purchaseContainer.dataset.signature === purchaseSignature) {
      return;
    }

    purchaseContainer.innerHTML = `
      <form method="post" action="/cart/add" data-rcc-recent-form data-product-handle="${escapeHtml(handle)}">
        ${renderOptionSelectors(optionGroups, initialSelections)}
        <p class="rcc-recsai__selection-message" data-rcc-selection-message ${requiresSelection ? "" : "hidden"}>
          ${escapeHtml(selectionMessage)}
        </p>
        <input type="hidden" name="quantity" value="1">
        <input type="hidden" name="id" value="${selectedVariant ? escapeHtml(selectedVariant.id) : ""}" data-rcc-variant-input>
        <button
          type="button"
          name="add"
          class="rcc-recsai__button add_to_cart ajax-submit global-button"
          data-rcc-add-to-cart
          ${selectedVariant ? "" : "disabled"}
        >
          <span class="text">${escapeHtml(addToCartLabel)}</span>
        </button>
      </form>
    `;
    purchaseContainer.dataset.signature = purchaseSignature;
  }

  async function updatePurchaseFormState(article) {
    const form = article.querySelector("[data-rcc-recent-form]");
    if (!form) return;

    const handle = form.getAttribute("data-product-handle") || getProductHandle(article);
    if (!handle) return;

    const productData = await fetchProductJson(handle);
    if (!productData) return;

    const currentVariant = getCurrentVariant(productData, article);
    const optionGroups = getRenderableOptionGroups(productData, currentVariant);
    const selections = syncOptionAvailability(form, productData, currentVariant, optionGroups);

    const missingOptions = optionGroups
      .filter((optionGroup) => optionGroup.values.length > 1 && !selections[optionGroup.name])
      .map((optionGroup) => optionGroup.name);

    const selectionMessage = form.querySelector("[data-rcc-selection-message]");
    const variantInput = form.querySelector("[data-rcc-variant-input]");
    const addToCartButton = form.querySelector("[data-rcc-add-to-cart]");
    if (!selectionMessage || !variantInput || !addToCartButton) return;

    if (missingOptions.length) {
      variantInput.value = "";
      addToCartButton.disabled = true;
      selectionMessage.textContent = buildSelectionPrompt(missingOptions);
      selectionMessage.hidden = false;
      return;
    }

    const selectedVariant = findMatchingVariant(productData, currentVariant, selections);
    if (!selectedVariant) {
      variantInput.value = "";
      addToCartButton.disabled = true;
      selectionMessage.textContent = "Choose an available option combination.";
      selectionMessage.hidden = false;
      return;
    }

    variantInput.value = String(selectedVariant.id);
    addToCartButton.disabled = false;
    selectionMessage.hidden = true;
    selectionMessage.textContent = "";
  }

  async function enhanceArticle(article) {
    if (!article) return;
    await renderPurchaseForm(article);
    await updatePurchaseFormState(article);
  }

  function ensureAccordion(widget) {
    if (!widget) return;

    const grid = widget.querySelector(".rcc-recsai__grid");
    const info = widget.querySelector(".rcc-recsai__info");
    const arrows = widget.querySelector(".rcc-recsai__arrows");
    const track = widget.querySelector(".rcc-recsai__track");
    if (!grid || !info || !track) return;

    if (widget.dataset.rccAccordionReady === "1") return;

    // Remove hidden attribute so CSS transitions control arrow visibility
    if (arrows) {
      arrows.removeAttribute("hidden");
    }

    let controls = grid.querySelector(".rcc-recsai__controls");
    if (!controls) {
      controls = document.createElement("div");
      controls.className = "rcc-recsai__controls";
      info.insertAdjacentElement("afterend", controls);
    }

    let toggle = controls.querySelector(".rcc-recsai__accordion-toggle");
    if (!toggle) {
      toggle = document.createElement("button");
      toggle.type = "button";
      toggle.className = "rcc-recsai__accordion-toggle";
      toggle.setAttribute("aria-expanded", "true");
      toggle.setAttribute("aria-label", "Toggle recently viewed products");
      toggle.innerHTML = `
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true">
          <path d="M6 14.5L12 8.5L18 14.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path>
        </svg>
      `;
      controls.appendChild(toggle);
    }

    function doToggle() {
      const isExpanded = toggle.getAttribute("aria-expanded") !== "false";
      toggle.setAttribute("aria-expanded", isExpanded ? "false" : "true");
      widget.dataset.rccCollapsed = isExpanded ? "true" : "false";
      // CSS handles arrows visibility via [data-rcc-collapsed] — no hidden attribute needed
    }

    if (!toggle.dataset.bound) {
      toggle.dataset.bound = "1";
      toggle.addEventListener("click", doToggle);
    }

    // Make the full info/title area clickable, matching CAV header behaviour
    if (!info.dataset.bound) {
      info.dataset.bound = "1";
      info.addEventListener("click", doToggle);
    }

    // Unified header row hover: add class to grid when cursor is over info or controls
    if (!grid.dataset.hoverBound) {
      grid.dataset.hoverBound = "1";
      const addHover    = () => grid.classList.add("rcc-header-hover");
      const removeHover = () => grid.classList.remove("rcc-header-hover");
      info.addEventListener("mouseenter", addHover);
      info.addEventListener("mouseleave", removeHover);
      controls.addEventListener("mouseenter", addHover);
      controls.addEventListener("mouseleave", removeHover);
    }

    widget.dataset.rccCollapsed = "false";
    widget.dataset.rccAccordionReady = "1";
  }

  function enhanceWidget(widget) {
    if (!widget) return;

    ensureAccordion(widget);

    const articles = Array.from(widget.querySelectorAll(RCC_RECENT_PRODUCT_SELECTOR));
    articles.forEach((article) => {
      enhanceArticle(article);
    });
  }

  function refreshRecentlyViewedWidgets() {
    scheduledRefresh = false;
    document.querySelectorAll(RCC_RECENT_WIDGET_SELECTOR).forEach((widget) => {
      enhanceWidget(widget);
    });
  }

  function scheduleRefresh() {
    if (scheduledRefresh) return;
    scheduledRefresh = true;
    requestAnimationFrame(refreshRecentlyViewedWidgets);
  }

  function initRecentlyViewedEnhancements() {
    scheduleRefresh();

    document.body.addEventListener("change", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLSelectElement)) return;
      if (!target.hasAttribute("data-rcc-option-name")) return;

      const article = target.closest(RCC_RECENT_PRODUCT_SELECTOR);
      if (!article) return;

      updatePurchaseFormState(article);
    });

    document.body.addEventListener("rcc:variant-change", (event) => {
      const article = event.target.closest(RCC_RECENT_PRODUCT_SELECTOR);
      if (!article) return;

      enhanceArticle(article);
    });

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.addedNodes.length || mutation.removedNodes.length) {
          scheduleRefresh();
          break;
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initRecentlyViewedEnhancements);
  } else {
    initRecentlyViewedEnhancements();
  }
})();


// Evan's Facet Opener

(function () {
  /**
   * Configure which facets should be open on load.
   *
   * You can target by:
   * - facet "key" matching the modifier class:
   *     .rcc-search__facet--sizes
   *     .rcc-search__facet--brands
   *     .rcc-search__facet--price
   * - OR by button id prefix:
   *     sizes-title
   *     brands-title
   *     price-title
   *
   * Use the exact suffix part you see in:
   *   rcc-search__facet--YOUR_KEY
   */
  var FACETS_TO_OPEN = [
    "sizes",
    "colorFamilies",
    // Examples from our HTML:
    // "genders",
    // "attributesshoeCategory",
    // "attributesshoeStyle",
    // "attributesactivity",
    // "attributesoptionWidth",
    // "attributesfeatures",
    // "colorfamilies",
    // "attributeseuShoeSize"
  ];

  var ROOT_SELECTOR = "#filter-content";
  var FACET_SELECTOR = ".rcc-search__facet";
  var TITLE_SELECTOR = ".rcc-search__facet__title";

  function unique(arr) {
    return Array.from(new Set(arr));
  }

  function findFacetTitleButtonByKey(root, key) {
    // 1) Try modifier class
    var facetByClass = root.querySelector(FACET_SELECTOR + ".rcc-search__facet--" + CSS.escape(key));
    if (facetByClass) {
      var btn = facetByClass.querySelector(TITLE_SELECTOR);
      if (btn) return btn;
    }

    // 2) Try id pattern: `${key}-title`
    var btnById = root.querySelector("#" + CSS.escape(key + "-title"));
    if (btnById) return btnById;

    // 3) Fuzzy match based on id containing key
    var allBtns = root.querySelectorAll(TITLE_SELECTOR);
    for (var i = 0; i < allBtns.length; i++) {
      var id = allBtns[i].id || "";
      if (id.toLowerCase().indexOf(key.toLowerCase()) !== -1) {
        return allBtns[i];
      }
    }

    return null;
  }

  function isButtonClosed(btn) {
    // Your HTML uses isOpen/isClosed classes.
    // We also defensively check aria-label text.
    if (btn.classList.contains("isClosed")) return true;
    if (btn.classList.contains("isOpen")) return false;

    var aria = (btn.getAttribute("aria-label") || "").toLowerCase();
    if (aria.indexOf("collapsed") !== -1) return true;
    if (aria.indexOf("expanded") !== -1) return false;

    // Unknown state, treat as closed to be safe.
    return true;
  }

  function openFacetButton(btn) {
    if (!btn) return false;

    // Only click if closed so we do not toggle an already-open facet shut.
    if (isButtonClosed(btn)) {
      btn.click();
      return true;
    }
    return false;
  }

  function openDesiredFacets(root) {
    var keys = unique(FACETS_TO_OPEN);
    var anyOpened = false;

    keys.forEach(function (key) {
      var btn = findFacetTitleButtonByKey(root, key);
      if (btn) {
        var opened = openFacetButton(btn);
        anyOpened = anyOpened || opened;
      }
    });

    return anyOpened;
  }

  function init() {
    var root = document.querySelector(ROOT_SELECTOR);
    if (!root) return;

    // Run once immediately in case facets are already present.
    openDesiredFacets(root);

    // Observe for late-rendered facets and open again.
    var observer = new MutationObserver(function () {
      openDesiredFacets(root);
    });

    observer.observe(root, { childList: true, subtree: true });

    // Safety stop to avoid observing forever.
    window.setTimeout(function () {
      observer.disconnect();
    }, 5000);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
